'use strict';

const { GatewayClient }      = require('./http');
const { VendorModule }       = require('./modules/VendorModule');
const { WalletModule }       = require('./modules/WalletModule');
const { DmtModule }          = require('./modules/DmtModule');
const { AepsModule }         = require('./modules/AepsModule');
const { UpiModule }          = require('./modules/UpiModule');
const { QrModule }           = require('./modules/QrModule');
const { CommissionModule }   = require('./modules/CommissionModule');
const { RegistrationModule } = require('./modules/RegistrationModule');
const { OnboardingModule }   = require('./modules/OnboardingModule');

/**
 * BucksBoxClient — main entry point for the BucksBox SDK.
 *
 * @example
 * const { BucksBoxClient } = require('@bucksbox/sdk');
 *
 * // clientId/key are issued at merchant registration (paymentSystem's
 * // User.apiKey / User.SaltAESKey) — this is a checkout-widget style SDK, so
 * // it expects those to already exist, the same way you'd configure a
 * // Stripe/Razorpay client with API keys from your dashboard rather than
 * // signing up through the payment SDK itself. There's no separate
 * // login/JWT step — the gateway authenticates every call from clientId +
 * // key alone (checksumSecret defaults to key — paymentSystem only ever
 * // issues one secret per account, reused for both).
 * //
 * // clientId and key are different paymentSystem fields: clientId
 * // identifies *who* is calling (User.apiKey, sent as X-Client-Id); key
 * // here is *what encrypts* the call (User.SaltAESKey, the actual
 * // AES-256-GCM key) — get this one wrong and the gateway can't
 * // decrypt/validate anything, so every transaction fails.
 * const client = new BucksBoxClient({
 *   environment: 'production', // or 'staging' — resolves to the right gateway URL internally.
 *                               // Pass gatewayUrl instead for local dev or any other URL.
 *   clientId:    'your-client-id',
 *   key:         'your-64-char-hex-aes-key',
 * });
 *
 * const balance = await client.vendor.getBalance();
 * const result  = await client.dmt.transactionInstant({ senderId, BeneficiaryId, amount, refId, otp, ... });
 */
class BucksBoxClient {
  /**
   * @param {object} config
   * @param {string} [config.gatewayUrl]       - explicit fintech-gateway base URL (the only URL this SDK talks
   *                                              to — the gateway proxies every call through to paymentSystem).
   *                                              Takes priority over `environment` when both are given.
   * @param {string} [config.environment]      - 'production' | 'staging' — resolves to the matching gateway URL
   *                                              internally instead of hardcoding one. One of gatewayUrl/
   *                                              environment is required.
   * @param {string} config.clientId           - paymentSystem's User.apiKey for this merchant/vendor. For
   *                                              `client.registration.register()` (self-service merchant
   *                                              onboarding), this is the onboarding *vendor's* own apiKey —
   *                                              the new merchant is linked to that vendor server-side.
   * @param {string} config.key                - paymentSystem's User.SaltAESKey (32-byte hex/base64 AES-256 key,
   *                                              used internally for encryption). `apiKey` and `aesKey` are both
   *                                              still accepted as deprecated aliases for this same field. Not
   *                                              the same thing as clientId — see the class doc above.
   * @param {string} [config.checksumSecret]   - defaults to key (paymentSystem issues one secret, reused for
   *                                              both AES-256-GCM and the HMAC-SHA256 checksum) — only pass a
   *                                              different value if your gateway's key provider genuinely uses
   *                                              two separate secrets.
   * @param {string} [config.merchantId]       - optional — only for a vendor clientId calling on behalf of
   *                                              one merchant (a vendor apiKey has no merchantId of its own).
   *                                              Automatically included in every call's body when set; omitted
   *                                              entirely otherwise. Leave unset for a merchant clientId.
   * @param {number} [config.timeout=30000]    - request timeout in ms
   */
  constructor(config) {
    if (!config.gatewayUrl && !config.environment) {
      throw new Error('BucksBoxClient: gatewayUrl or environment is required');
    }
    if (!config.clientId || !(config.key || config.apiKey || config.aesKey)) {
      throw new Error('BucksBoxClient: clientId and key are required');
    }

    this._http = new GatewayClient(config);

    this.vendor       = new VendorModule(this._http);
    this.wallet       = new WalletModule(this._http);
    this.dmt          = new DmtModule(this._http);
    this.aeps         = new AepsModule(this._http);
    this.upi          = new UpiModule(this._http);
    /** @deprecated use client.upi */
    this.qr           = new QrModule(this._http);
    this.commission   = new CommissionModule(this._http);
    this.registration = new RegistrationModule(this._http);
    this.onboarding   = new OnboardingModule(this._http);
  }
}

module.exports = { BucksBoxClient };
